import animationManager, {
  AnimationStatusCallbacks,
  makeSequence,
  SequenceStep
} from '../../services/animations/animation-manager';
import { AnimationSequences, AnimationSequencesType } from '../../utility/constants';

export type RouteChangeCallbacks = {
  onSlideMainDrawerOutEnd: () => Promise<unknown>;
};

const resetToFarRight = (el: HTMLElement) => {
  el.style.left = '150%';
  el.style.position = 'relative';
};

const mainDrawerHTMLSelector = '#main-drawer > div:first-child';

export const registerOpenDoubleDrawerSequence = ({
  onAnimationStarts,
  onAnimationEnds
}: Required<AnimationStatusCallbacks>): void => {
  const sequence: SequenceStep[] = [
    [
      {
        onStart: onAnimationStarts,
        elementSelector: 'phn-double-drawer',
        animation: {
          className: 'opening-animation',
          cleanUpAfterAnimation: (element) => {
            element.style.left = '0px';
          }
        }
      },
      {
        elementSelector: 'phn-backdrop',
        animation: {
          className: 'fade-in-animation',
          cleanUpAfterAnimation: (element) => {
            element.style.opacity = '1';
            element.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
          }
        },
        onEnd: onAnimationEnds
      }
    ]
  ];

  animationManager.register(AnimationSequences.OPEN_DOUBLE_DRAWER, makeSequence(sequence));
};

export const registerCloseDoubleDrawerSequence = ({
  onBackdropFadeOutEnd,
  onAnimationStarts,
  onAnimationEnds
}: Required<AnimationStatusCallbacks> & {
  onBackdropFadeOutEnd: () => Promise<void>;
}): void => {
  const sequence: SequenceStep[] = [
    [
      {
        elementSelector: 'phn-double-drawer',
        onStart: onAnimationStarts,
        animation: {
          className: 'closing-animation',
          cleanUpBeforeAnimation: (element) => {
            element.style.left = '';
          }
        }
      },
      {
        elementSelector: 'phn-backdrop',
        animation: {
          className: 'fade-out-animation',
          cleanUpBeforeAnimation: (element) => {
            element.style.opacity = '';
            element.style.backgroundColor = '';
          }
        },
        onEnd: () => {
          onBackdropFadeOutEnd();
          onAnimationEnds();
        }
      }
    ]
  ];

  animationManager.register(AnimationSequences.CLOSE_DOUBLE_DRAWER, makeSequence(sequence));
};

export const registerTransitionSequence = (
  { onSlideMainDrawerOutEnd }: RouteChangeCallbacks,
  direction: 'normal' | 'reverse' = 'normal'
): void => {
  animationManager.register(
    `transitionSameLevelDoubleDrawer${direction === 'reverse' ? 'Reverse' : ''}`,
    makeSequence([
      [
        {
          elementSelector: mainDrawerHTMLSelector,
          animation: {
            className:
              direction === 'reverse' ? 'transition-backward-animation-out' : 'transition-forward-animation-out',
            cleanUpAfterAnimation: resetToFarRight
          },
          onEnd: onSlideMainDrawerOutEnd
        }
      ],
      [
        {
          elementSelector: mainDrawerHTMLSelector,
          animation: {
            className: direction === 'reverse' ? 'transition-forward-animation-in' : 'transition-forward-animation-in',
            cleanUpBeforeAnimation: (element) => {
              element.style.left = '';
              element.style.position = '';
            }
          }
        }
      ]
    ])
  );
};

export const registerForwardToNextMenuSequence = ({ onSlideMainDrawerOutEnd }: RouteChangeCallbacks): void => {
  animationManager.register(
    'transitionNextLevelDoubleDrawer',
    makeSequence([
      [
        {
          elementSelector: '#side-drawer > div',
          animation: {
            className: 'transition-forward-animation-out'
          }
        },
        {
          elementSelector: mainDrawerHTMLSelector,
          animation: {
            className: 'transition-forward-animation-out'
          },
          onEnd: onSlideMainDrawerOutEnd
        }
      ],
      [
        {
          elementSelector: '#side-drawer > div',
          animation: {
            className: 'transition-forward-animation-in'
          }
        },
        {
          elementSelector: mainDrawerHTMLSelector,
          animation: {
            className: 'transition-forward-animation-in'
          }
        }
      ]
    ])
  );
};
export const registerBackToPreviousMenuSequence = ({ onSlideMainDrawerOutEnd }: RouteChangeCallbacks): void => {
  animationManager.register(
    'transitionPreviousLevelDoubleDrawer',
    makeSequence([
      [
        {
          elementSelector: '#side-drawer > div',
          animation: {
            className: 'transition-backward-animation-out'
          }
        },
        {
          elementSelector: mainDrawerHTMLSelector,
          animation: {
            className: 'transition-backward-animation-out'
          },
          onEnd: onSlideMainDrawerOutEnd
        }
      ],
      [
        {
          elementSelector: '#side-drawer > div',
          animation: {
            className: 'transition-backward-animation-in'
          }
        },
        {
          elementSelector: mainDrawerHTMLSelector,
          animation: {
            className: 'transition-backward-animation-in'
          }
        }
      ]
    ])
  );
};

export const isPlayingDoubleDrawerAnimation = (): boolean => {
  const doubleDrawerAnimationSequences: AnimationSequencesType[] = [
    AnimationSequences.OPEN_DOUBLE_DRAWER,
    AnimationSequences.CLOSE_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_NEXT_LEVEL_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_PREVIOUS_LEVEL_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_SAME_LEVEL_DOUBLE_DRAWER,
    AnimationSequences.TRANSITION_SAME_LEVEL_DOUBLE_DRAWER_REVERSE
  ];

  return doubleDrawerAnimationSequences.some((sequence) => {
    try {
      return animationManager.isPlaying(sequence);
    } catch {
      return false;
    }
  });
};
